import { ProjectSocialSpendCommitmentType } from "../../interfaces/projectSocialSpendCommitment/enums";
/**
 * A criteria set carries only the FILTER half — a plain field rename both ways.
 * `certifications` and `entityTypes` default to `[]` as they do on the
 * commitment, because the parent's employee-cost CHECK tests
 * `cardinality(...) = 0` and not `IS NULL`.
 *
 * NOT sorted here: `criteriaSetsExpr` in the entity handler aggregates
 * `ORDER BY ordinal`, so the array arrives in the authoring form's tab order.
 */
const decodeCriteriaSet = (criteriaSet) => ({
    abns: criteriaSet.abns,
    certifications: criteriaSet.certifications || [],
    employmentLevel: criteriaSet.employment_level,
    entityTypes: criteriaSet.entity_types || [],
    excludedPostCodes: criteriaSet.excluded_post_codes,
    factorCountingMethod: criteriaSet.factor_counting_method,
    gender: criteriaSet.gender,
    id: criteriaSet.project_social_spend_commitment_criteria_set_id,
    maxDistance: criteriaSet.max_distance,
    newEntrantToConstruction: criteriaSet.new_entrant_to_construction,
    occupation: criteriaSet.occupation,
    occupationType: criteriaSet.occupation_type,
    ordinal: criteriaSet.ordinal,
    postCodes: criteriaSet.post_codes,
    socialFactors: criteriaSet.social_factors
});
const encodeCriteriaSet = (criteriaSet, commitmentId) => ({
    abns: criteriaSet.abns,
    certifications: criteriaSet.certifications || [],
    employment_level: criteriaSet.employmentLevel,
    entity_types: criteriaSet.entityTypes || [],
    excluded_post_codes: criteriaSet.excludedPostCodes,
    factor_counting_method: criteriaSet.factorCountingMethod,
    gender: criteriaSet.gender,
    max_distance: criteriaSet.maxDistance,
    new_entrant_to_construction: criteriaSet.newEntrantToConstruction,
    occupation: criteriaSet.occupation,
    occupation_type: criteriaSet.occupationType,
    ordinal: criteriaSet.ordinal,
    post_codes: criteriaSet.postCodes,
    project_social_spend_commitment_criteria_set_id: criteriaSet.id,
    project_social_spend_commitment_id: commitmentId,
    social_factors: criteriaSet.socialFactors
});
export const decodeProjectSocialSpendCommitment = (socialCommitment) => {
    return {
        abns: socialCommitment.abns,
        certifications: socialCommitment.certifications || [],
        colour: socialCommitment.colour,
        created: new Date(socialCommitment.created),
        criteriaSets: socialCommitment.criteriaSets?.map(decodeCriteriaSet),
        employmentLevel: socialCommitment.employment_level,
        entityTypes: socialCommitment.entity_types || [],
        excludedPostCodes: socialCommitment.excluded_post_codes,
        factorCountingMethod: socialCommitment.factor_counting_method,
        gender: socialCommitment.gender,
        generationSource: socialCommitment.generation_source,
        id: socialCommitment.project_social_spend_commitment_id,
        maxDistance: socialCommitment.max_distance,
        newEntrantToConstruction: socialCommitment.new_entrant_to_construction,
        occupation: socialCommitment.occupation,
        occupationType: socialCommitment.occupation_type,
        postCodes: socialCommitment.post_codes,
        projectId: socialCommitment.project_id,
        socialFactors: socialCommitment.social_factors,
        sourceFrameworkId: socialCommitment.source_framework_id,
        sourceFrameworkTargetId: socialCommitment.source_framework_target_id,
        targetDescription: socialCommitment.target_description
            ? socialCommitment.target_description
            : undefined,
        targetMetricType: socialCommitment.target_metric_type ??
            ProjectSocialSpendCommitmentType.Invoice,
        targetName: socialCommitment.target_type,
        targetValue: socialCommitment.target_value,
        targetValueRealised: socialCommitment.target_value_realised
    };
};
export const encodeProjectSocialSpendCommitment = (socialCommitment) => {
    return {
        abns: socialCommitment.abns,
        certifications: socialCommitment.certifications || [],
        colour: socialCommitment.colour,
        created: socialCommitment.created,
        /**
         * Sets 2..N are stored from here. Set 1 is stored from the flat fields,
         * never from this array's first entry — see `criteriaSetFilters` in the
         * backend and `decisions.md` §28.
         */
        criteriaSets: socialCommitment.criteriaSets?.map((criteriaSet) => encodeCriteriaSet(criteriaSet, socialCommitment.id)),
        employment_level: socialCommitment.employmentLevel,
        entity_types: socialCommitment.entityTypes || [],
        excluded_post_codes: socialCommitment.excludedPostCodes,
        factor_counting_method: socialCommitment.factorCountingMethod,
        gender: socialCommitment.gender,
        generation_source: socialCommitment.generationSource,
        max_distance: socialCommitment.maxDistance,
        new_entrant_to_construction: socialCommitment.newEntrantToConstruction,
        occupation: socialCommitment.occupation,
        occupation_type: socialCommitment.occupationType,
        post_codes: socialCommitment.postCodes,
        project_id: socialCommitment.projectId,
        project_social_spend_commitment_id: socialCommitment.id,
        social_factors: socialCommitment.socialFactors,
        source_framework_id: socialCommitment.sourceFrameworkId,
        source_framework_target_id: socialCommitment.sourceFrameworkTargetId,
        target_description: socialCommitment.targetDescription,
        target_metric_type: socialCommitment.targetMetricType,
        target_type: socialCommitment.targetName,
        target_value: socialCommitment.targetValue,
        target_value_realised: socialCommitment.targetValueRealised
    };
};
